/**
 * Class description
 *
 * Created: 2018-03-13
 * @author jonas
 */
import {DungeonQuestTextStyles} from "../../config/DungeonQuestTextStyles";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {DungeonQuestLayerConfig} from "../../config/DungeonQuestLayerConfig";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {DungeonQuest} from "../DungeonQuest";
import {Linear, Power2, TimelineLite, TweenLite} from "gsap"
import {DualLayerBitmapCountUp} from "../display/DualLayerBitmapCountUp";
import {TweenParticleEmitter} from "../display/TweenParticleEmitter";
import {DungeonQuestParticleConfig} from "../../config/DungeonQuestParticleConfig";
import {DungeonQuestGameAssets} from "../DungeonQuestGameAssets";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {ShakeUtil} from "../../utils/ShakeUtil";
import {ShakeEffect} from "../display/effects/ShakeEffect";
import {DungeonQuestGameConfig} from "../DungeonQuestGameConfig";

export class WinCountUpDisplay extends BaseView {
    private _layer:ScalableLayer;
    private _scaleContainer:PIXI.Container;
    private _countText:DualLayerBitmapCountUp;
    private _hasStopped:boolean;
    private _coinParticles:TweenParticleEmitter;
    private _shakeEffect:ShakeEffect;


    get shakeEffect():ShakeEffect {
        if(!this._shakeEffect) {
            this._shakeEffect = ShakeUtil.createDefaultShake(2, 0.03);
        }
        return this._shakeEffect;
    }

    constructor() {
        super();
        this._layer = StageManager.getLayer(DungeonQuestLayerConfig.WIN_DISPLAY.name);
        this._layer.addChild(this);
    }

    protected initAnimations() {
        this._scaleContainer = new PIXI.Container();
        this._scaleContainer.position.set(360, 450);
        this._scaleContainer.alpha = 0;

        this._countText = new DualLayerBitmapCountUp(DungeonQuestTextStyles.WIN_DISPLAY_NUMBERS_WHITE, DungeonQuestTextStyles.WIN_DISPLAY_NUMBERS, 700);
        this._countText.getFadeOutTopLayer(0.01);

        const particleTextures = [
            GameResources.getTextures(DungeonQuestGameAssets.BW_PARTICLE_1)[0],
            GameResources.getTextures(DungeonQuestGameAssets.BW_PARTICLE_2)[0],
            GameResources.getTextures(DungeonQuestGameAssets.BW_PARTICLE_3)[0],
            GameResources.getTextures(DungeonQuestGameAssets.BW_PARTICLE_4)[0]
        ];

        this._coinParticles = new TweenParticleEmitter(particleTextures, DungeonQuestParticleConfig.winDisplayParticleConfig);
        this._coinParticles.position.set(360, 450);

        this._scaleContainer.addChild(this._countText);
        this.addChild(this._coinParticles);
        this.addChild(this._scaleContainer);
    }


    public getWinDisplay(from:number, to:number, duration:number, winType:WinRatioType, continueCounting:boolean = false):TimelineLite {
        this._hasStopped = false;
        const tl:TimelineLite = new TimelineLite();
        const defaultScale = 1;

        const scaleTl:TimelineLite = new TimelineLite();

        const showTl:TimelineLite = new TimelineLite();
        if(!continueCounting) {
            showTl.add(() => this._scaleContainer.scale.set(0));
            showTl.add(() => this._scaleContainer.alpha = 0);
        }
        showTl.add([
            new TweenLite(this._scaleContainer.scale, 0.3, {
                x : defaultScale * 0.6,
                y : defaultScale * 0.6,
                ease : Power2.easeOut
            }),
            new TweenLite(this._scaleContainer, 0.3, {alpha : 1, ease : Linear.easeOut})
        ]);

        scaleTl.add(showTl);
        let localDuration = Math.abs(duration - showTl.totalDuration()); // #99
        scaleTl.add(new TweenLite(this._scaleContainer.scale, localDuration, {
            x : defaultScale * 0.8,
            y : defaultScale * 0.8,
            ease : Linear.easeIn
        }));


        const count:TimelineLite = new TimelineLite();

        // #99
        if (!DungeonQuestGameConfig.instance.WIN_BELOW_STAKE) {
            count.add(() => DungeonQuest.soundPlayer.startCountUp());
        }

        count.add(this._countText.getCountAnimation(from, to, duration));
        count.add([
            new TweenLite(this._scaleContainer.scale, 0.04, {x : 1, y : 1, ease : Linear.easeNone}),
            () => this.stopCountAt(to, winType)
        ]);
        tl.add([scaleTl, count]);

        return tl;
    }

    public getEndAnimation(totalAmount:number, winType:WinRatioType, delay:number = 0.5):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            new TweenLite(this._scaleContainer.scale, 0.02, {x : 1, y : 1, ease : Power2.easeIn}),
            new TweenLite(this._scaleContainer, 0.02, {alpha : 1, ease : Linear.easeOut})
        ]);
        tl.add(() => this.stopCountAt(totalAmount, winType));
        tl.add([
            new TweenLite(this._scaleContainer.scale, 0.2, {x : 0, y : 0, ease : Power2.easeIn}),
            new TweenLite(this._scaleContainer, 0.2, {alpha : 0, ease : Linear.easeOut})
        ], delay);
        return tl;
    }

    private stopCountAt(to:number, winType:WinRatioType) {
        if(!this._hasStopped) {
            this._hasStopped = true;
            this._countText.getFadeInAndOutTopLayer(0.2, 1, 0, 0.2);
            this.shakeEffect.getOneShotShake(0.2);

            // #99
            if (!DungeonQuestGameConfig.instance.WIN_BELOW_STAKE) {
                const emitterSettings: { scale: number, maxParticles: number } = this.getFrequencyForWinType(winType);
                this._coinParticles.emitterConfig.scale.start = emitterSettings.scale;
                this._coinParticles.emitterConfig.scale.end = emitterSettings.scale;
                this._coinParticles.emitterConfig.maxParticles = emitterSettings.maxParticles;
                this._coinParticles.updateConfig();
                this._coinParticles.start();
            }
        }
        this._countText.stopAt(to);

        // #99
        if (!DungeonQuestGameConfig.instance.WIN_BELOW_STAKE) {
            DungeonQuest.soundPlayer.stopCountUp();
        }
    }

    private getFrequencyForWinType(winType:WinRatioType):{ scale:number, maxParticles:number } {
        switch(winType) {
            case WinRatioType.LARGE :
                return {scale : 0.5, maxParticles : 5};

            case WinRatioType.MEDIUM :
                return {scale : 0.5, maxParticles : 4};

            case WinRatioType.SMALL :
                return {scale : 0.5, maxParticles : 3};

            default :
                return {scale : 0.5, maxParticles : 2};
        }
    }
}